import React, { useState } from 'react';
import {
  Calendar,
  MapPin,
  Award,
  Heart,
  MessageCircle,
  Share2,
  CheckCircle,
  Clock,
  Users,
  Star,
  X,
  ZoomIn
} from 'lucide-react';
import { toast } from 'react-toastify';

interface Event {
  id: number;
  title: string;
  description?: string;
  event_date: string;
  event_time?: string;
  location?: string;
  category?: string;
  points: number;
  capacity?: number;
  brochure_url?: string;
  brochureUrl?: string;
  status: 'upcoming' | 'ongoing' | 'completed';
  attendanceCount?: number;
  isRegistered?: boolean;
  hasAttended?: boolean;
  my_rsvp?: 'confirmed' | 'declined' | 'interested' | null;
  myRsvp?: 'confirmed' | 'declined' | 'interested' | null;
}

interface EventCardProps {
  event: Event;
  onRegister: (eventId: number) => void;
  onRSVP?: (eventId: number, status: 'confirmed' | 'declined' | 'interested') => void;
  onLike?: (eventId: number) => void;
  onComment?: (eventId: number) => void;
  onShare?: (eventId: number) => void;
}

export const EventCard: React.FC<EventCardProps> = ({
  event,
  onRegister,
  onRSVP,
  onLike,
  onComment,
  onShare
}) => {
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(Math.floor(Math.random() * 50) + 10);
  const [showRSVP, setShowRSVP] = useState(false);
  const [showPosterModal, setShowPosterModal] = useState(false);
  const rsvpStatus = event.my_rsvp || event.myRsvp;
  const posterUrl = event.brochure_url || event.brochureUrl;

  const handleLike = () => {
    setIsLiked(!isLiked);
    setLikeCount(isLiked ? likeCount - 1 : likeCount + 1);
    if (onLike) onLike(event.id);
    toast.success(isLiked ? 'Removed from favorites' : 'Added to favorites');
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: event.title, text: `Join me at ${event.title}!`, url: window.location.href }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
    if (onShare) onShare(event.id);
  };

  const handleComment = () => {
    if (onComment) onComment(event.id);
    toast.info('Comments feature coming soon!');
  };

  const getStatusBadge = () => {
    switch (event.status) {
      case 'upcoming':  return <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">Upcoming</span>;
      case 'ongoing':   return <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full animate-pulse">Live Now</span>;
      case 'completed': return <span className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-semibold rounded-full">Completed</span>;
    }
  };

  return (
    <>
      <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group">

        {/* ── Poster / Header ── */}
        {posterUrl ? (
          <div
            className="relative w-full cursor-pointer"
            style={{ background: '#0a0a0a' }}
            onClick={() => setShowPosterModal(true)}
          >
            <img
              src={posterUrl}
              alt={event.title}
              className="w-full"
              style={{ display: 'block', maxHeight: 420, objectFit: 'contain', width: '100%' }}
            />
            {/* Hover zoom hint */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/60 rounded-full p-3">
                <ZoomIn size={22} color="white" />
              </div>
            </div>
            {/* Status badge */}
            <div className="absolute top-3 left-3 z-10">{getStatusBadge()}</div>
            {event.hasAttended && (
              <div className="absolute top-3 right-3 z-10 px-3 py-1 bg-white/90 backdrop-blur-sm text-green-600 text-xs font-semibold rounded-full flex items-center gap-1">
                <CheckCircle size={14} /> Attended
              </div>
            )}
          </div>
        ) : (
          <div className="relative h-44 overflow-hidden bg-gradient-to-br from-cyan-500 via-blue-500 to-purple-600">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full" />
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/10 rounded-full" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            <div className="absolute top-4 left-4 right-4 flex justify-between items-start z-10">
              {getStatusBadge()}
              {event.hasAttended && (
                <div className="px-3 py-1 bg-white/90 backdrop-blur-sm text-green-600 text-xs font-semibold rounded-full flex items-center gap-1">
                  <CheckCircle size={14} /> Attended
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Content ── */}
        <div className="p-6">
          {/* Title and category */}
          <div className="mb-4">
            {event.category && (
              <span className="inline-block px-2 py-1 bg-purple-50 text-purple-600 text-xs font-medium rounded mb-2">
                {event.category}
              </span>
            )}
            <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
              {event.title}
            </h3>
            {event.description && (
              <p className="text-sm text-gray-600 leading-relaxed">{event.description}</p>
            )}
          </div>

          {/* Event details */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="flex items-center gap-2 text-sm text-gray-600 col-span-2">
              <Calendar size={16} className="text-blue-500 flex-shrink-0" />
              <span>{new Date(event.event_date).toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' })}</span>
            </div>
            {event.event_time && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock size={16} className="text-green-500" />
                <span>{event.event_time}</span>
              </div>
            )}
            {event.location && (
              <div className="flex items-center gap-2 text-sm text-gray-600 col-span-2">
                <MapPin size={16} className="text-red-500 flex-shrink-0" />
                <span>{event.location}</span>
              </div>
            )}
          </div>

          {/* Points and capacity */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-lg">
              <Award size={18} className="text-amber-600" />
              <span className="text-sm font-semibold text-amber-700">{event.points} Points</span>
            </div>
            {event.capacity && (
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg">
                <Users size={18} className="text-blue-600" />
                <span className="text-sm font-semibold text-blue-700">
                  {event.attendanceCount || 0}/{event.capacity}
                </span>
              </div>
            )}
          </div>

          {/* Interaction buttons */}
          <div className="flex items-center gap-1 mb-4 pb-4 border-b border-gray-100">
            <button
              onClick={handleLike}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-all ${isLiked ? 'bg-red-50 text-red-600' : 'hover:bg-gray-50 text-gray-600'}`}
            >
              <Heart size={18} fill={isLiked ? 'currentColor' : 'none'} />
              <span className="text-sm font-medium">{likeCount}</span>
            </button>
            <button
              onClick={handleComment}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 text-gray-600 transition-all"
            >
              <MessageCircle size={18} />
              <span className="text-sm font-medium">Comment</span>
            </button>
            <button
              onClick={handleShare}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 text-gray-600 transition-all"
            >
              <Share2 size={18} />
              <span className="text-sm font-medium">Share</span>
            </button>
          </div>

          {/* RSVP Buttons */}
          {event.status === 'upcoming' && !event.hasAttended && onRSVP && (
            <div className="space-y-2">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Your Response</p>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => onRSVP(event.id, 'confirmed')}
                  className={`flex flex-col items-center gap-1 px-3 py-2.5 rounded-lg font-medium text-xs transition-all ${rsvpStatus === 'confirmed' ? 'bg-green-500 text-white shadow-md' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}
                >
                  <CheckCircle size={16} />
                  <span>{rsvpStatus === 'confirmed' ? '✓ Going' : 'Going'}</span>
                </button>
                <button
                  onClick={() => onRSVP(event.id, 'interested')}
                  className={`flex flex-col items-center gap-1 px-3 py-2.5 rounded-lg font-medium text-xs transition-all ${rsvpStatus === 'interested' ? 'bg-yellow-500 text-white shadow-md' : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'}`}
                >
                  <Star size={16} />
                  <span>{rsvpStatus === 'interested' ? '⭐ Interested' : 'Interested'}</span>
                </button>
                <button
                  onClick={() => onRSVP(event.id, 'declined')}
                  className={`flex flex-col items-center gap-1 px-3 py-2.5 rounded-lg font-medium text-xs transition-all ${rsvpStatus === 'declined' ? 'bg-gray-500 text-white shadow-md' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}
                >
                  <span className="text-base">✕</span>
                  <span>{rsvpStatus === 'declined' ? '✓ Not Going' : 'Not Going'}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Poster + Detail Modal ── */}
      {showPosterModal && posterUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)' }}
          onClick={() => setShowPosterModal(false)}
        >
          <div
            className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl"
            style={{ background: '#111' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Close */}
            <button
              onClick={() => setShowPosterModal(false)}
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(0,0,0,0.6)' }}
            >
              <X size={18} color="white" />
            </button>

            {/* Full poster */}
            <img
              src={posterUrl}
              alt={event.title}
              className="w-full rounded-t-2xl"
              style={{ display: 'block' }}
            />

            {/* Event details below poster */}
            <div className="p-5 bg-white rounded-b-2xl">
              {/* Status + category row */}
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {getStatusBadge()}
                {event.category && (
                  <span className="px-2 py-1 bg-purple-50 text-purple-600 text-xs font-medium rounded">
                    {event.category}
                  </span>
                )}
                {event.hasAttended && (
                  <span className="px-2 py-1 bg-green-50 text-green-600 text-xs font-semibold rounded-full flex items-center gap-1">
                    <CheckCircle size={12} /> Attended
                  </span>
                )}
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-3">{event.title}</h2>

              {event.description && (
                <p className="text-sm text-gray-600 leading-relaxed mb-4">{event.description}</p>
              )}

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Calendar size={15} className="text-blue-500 flex-shrink-0" />
                  <span>{new Date(event.event_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span>
                </div>
                {event.event_time && (
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Clock size={15} className="text-green-500" />
                    <span>{event.event_time}</span>
                  </div>
                )}
                {event.location && (
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <MapPin size={15} className="text-red-500 flex-shrink-0" />
                    <span>{event.location}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-lg">
                  <Award size={16} className="text-amber-600" />
                  <span className="text-sm font-semibold text-amber-700">{event.points} Points</span>
                </div>
                {event.capacity && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg">
                    <Users size={16} className="text-blue-600" />
                    <span className="text-sm font-semibold text-blue-700">{event.attendanceCount || 0}/{event.capacity} spots</span>
                  </div>
                )}
              </div>

              {/* RSVP in modal */}
              {event.status === 'upcoming' && !event.hasAttended && onRSVP && (
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => { onRSVP(event.id, 'confirmed'); setShowPosterModal(false); }}
                    className={`flex flex-col items-center gap-1 px-3 py-2.5 rounded-lg font-medium text-xs transition-all ${rsvpStatus === 'confirmed' ? 'bg-green-500 text-white' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}
                  >
                    <CheckCircle size={16} />
                    <span>{rsvpStatus === 'confirmed' ? '✓ Going' : 'Going'}</span>
                  </button>
                  <button
                    onClick={() => { onRSVP(event.id, 'interested'); setShowPosterModal(false); }}
                    className={`flex flex-col items-center gap-1 px-3 py-2.5 rounded-lg font-medium text-xs transition-all ${rsvpStatus === 'interested' ? 'bg-yellow-500 text-white' : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'}`}
                  >
                    <Star size={16} />
                    <span>{rsvpStatus === 'interested' ? '⭐ Interested' : 'Interested'}</span>
                  </button>
                  <button
                    onClick={() => { onRSVP(event.id, 'declined'); setShowPosterModal(false); }}
                    className={`flex flex-col items-center gap-1 px-3 py-2.5 rounded-lg font-medium text-xs transition-all ${rsvpStatus === 'declined' ? 'bg-gray-500 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}
                  >
                    <span className="text-base">✕</span>
                    <span>{rsvpStatus === 'declined' ? '✓ Not Going' : 'Not Going'}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
